import { useState, useEffect, useRef, useMemo } from "react";
import styled from "styled-components";
import FilterIco from "assets/icons/filter-ico.svg";
import SearchBox from "components/common/inputs/searchInput/SearchBox";
import axios from "axios";
import NewSelectStatystics from "components/common/inputs/select/NewSelectStatystics";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import NewButton, {
  NewButtonSize,
  NewButtonVariant,
} from "components/common/buttons/basicButton/NewButton";
import { useNavbarContext } from "../../hooks/useNavbarContext";
import OpenMenuIcon from "../../assets/icons/OTWORZ.svg";
import CloseMenuIcon from "../../assets/icons/ZAMKNIJ.svg";

import { useDebounce } from "use-debounce";

import { getOtherConnectionUrl } from "api/BaseConnection";
import api from "services/axiosConfig";
import { useAcademyBranches } from "hooks/useAcademyBranches";

import HomeTableNew from "components/common/dataTable/HometableNew";
import { useSelector } from "react-redux";
import { IAvgStats, IPlayerStats, avgHomeStatsInitialValues } from "types/homeTable";

import { computeFormDisplay } from "hooks/useDateInputRange";

import NewCheckbox from "components/common/inputs/checkbox/NewCheckbox";

import WeeksMultiSelect from "./WeeksMultiSelect";

import {
  initialCheckboxes,
  CheckboxKey,
  CheckboxesState,
  HOME_COLS_LS_KEY,
  HOME_COLS_ORDER_LS_KEY,
  DEFAULT_HOME_COLUMN_ORDER,
  LOCKED_HOME_COLUMN_KEYS,
  normalizeHomeColumnOrder,
  normalizeHomeColsCheckboxes,
} from "components/common/dataTable/HomeInfoHelper";
import Button from "components/common/buttons/basicButton/Button";
import { ButtonVariant } from "components/common/buttons/buttonTypes";
import { GetOwnFolders } from "services/folderServices";
import { getWeeksInMonth } from "date-fns";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ReactComponent as EditIcon } from "assets/icons/edit.svg";
import { ReactComponent as CloseIcon } from "assets/icons/false.svg";
import { ReactComponent as DragDotsIcon } from "assets/icons/drag-dots.svg";
import { getLocalStorageValue, setLocalStorageValue } from "utils/localStorageUtils";

const FilterButtonWrapper = styled.div`
  position: relative;
  display: flex;
`;

const ActiveFilterDot = styled.span`
  position: absolute;
  top: -3px;
  right: -3px;
  width: 9px;
  height: 9px;
  border: 2px solid #202d38;
  border-radius: 50%;
  background: #e53935;
  pointer-events: none;
  z-index: 1;
`;

const BlurBox = styled.div<{ isMenuOpen: boolean }>`
  position: absolute;
  display: ${(props) => (props.isMenuOpen ? "unset" : "none")};
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  backdrop-filter: blur(3px);
  z-index: 99999;
`;

const SingleColumn = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  gap: 5px;

  & > p {
    text-align: center;
  }

  & > * {
    width: 280px;
  }
`;

const SortableColumnItemBox = styled.div<{
  isDragging: boolean;
  isInactiveInEditMode?: boolean;
}>`
  display: flex;
  align-items: center;
  min-width: 0;
  min-height: 32px;
  border-radius: 5px;
  background: ${({ isDragging }) => (isDragging ? "rgba(255, 255, 255, 0.08)" : "transparent")};
  opacity: ${({ isDragging, isInactiveInEditMode }) =>
    isDragging ? 0.75 : isInactiveInEditMode ? 0.45 : 1};
  z-index: ${({ isDragging }) => (isDragging ? 1 : "auto")};

  .new-checkbox-root {
    min-width: 0;
    flex: 1;
  }
`;

const ColumnDragHandle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  min-width: 24px;
  height: 28px;
  touch-action: none;
  cursor: grab;

  svg {
    width: 18px;
    height: 18px;
  }

  svg path {
    fill: rgba(248, 248, 248, 0.75);
  }

  &:active {
    cursor: grabbing;
  }
`;

const ColumnDragPlaceholder = styled.span`
  width: 24px;
  min-width: 24px;
  height: 28px;
`;

const ColumnEditLabel = styled.span`
  margin-left: 8px;
  color: white;
  font-size: 12px;
  white-space: nowrap;
`;

const ColumnListBox = styled.div<{ isEditMode: boolean }>`
  display: grid;
  justify-content: space-between;
  width: 280px;
  gap: 10px;
  padding-top: 20px;
  grid-template-columns: ${({ isEditMode }) => (isEditMode ? "1fr" : "1fr 1fr")};
  height: ${({ isEditMode }) => (isEditMode ? "550px" : "auto")};
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(145, 163, 182, 0.7) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(145, 163, 182, 0.7);
    border-radius: 8px;
  }
`;

interface SortableColumnItemProps {
  columnKey: CheckboxKey;
  item: CheckboxesState[CheckboxKey];
  isEditMode: boolean;
  onCheckboxChange: (key: CheckboxKey, checked: boolean) => void;
}

const LockedColumnItem: React.FC<SortableColumnItemProps> = ({
  columnKey,
  item,
  isEditMode,
  onCheckboxChange,
}) => {
  const { t } = useTranslation();
  const columnLabel = t(`home.cols.${columnKey}`);

  return (
    <SortableColumnItemBox isDragging={false} isInactiveInEditMode={isEditMode && !item.active}>
      {isEditMode ? (
        <>
          <ColumnDragPlaceholder aria-hidden="true" />
          <ColumnEditLabel>{columnLabel}</ColumnEditLabel>
        </>
      ) : (
        <NewCheckbox
          className="new-checkbox-label-font-size"
          checked={item.active}
          label={columnLabel}
          disabled={!!item.locked}
          onChange={(checked) => onCheckboxChange(columnKey, checked)}
        />
      )}
    </SortableColumnItemBox>
  );
};

const SortableColumnItem: React.FC<SortableColumnItemProps> = ({
  columnKey,
  item,
  isEditMode,
  onCheckboxChange,
}) => {
  const { t } = useTranslation();
  const columnLabel = t(`home.cols.${columnKey}`);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    setActivatorNodeRef,
  } = useSortable({
    id: columnKey,
    disabled: !isEditMode || !!item.locked,
  });

  return (
    <SortableColumnItemBox
      ref={setNodeRef}
      isDragging={isDragging}
      isInactiveInEditMode={isEditMode && !item.active}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      {!isEditMode && (
        <NewCheckbox
          className="new-checkbox-label-font-size"
          checked={item.active}
          label={columnLabel}
          disabled={!!item.locked}
          onChange={(checked) => onCheckboxChange(columnKey, checked)}
        />
      )}
      {isEditMode && (
        <>
          <ColumnDragHandle ref={setActivatorNodeRef} {...attributes} {...listeners}>
            <NewButton
              variant={NewButtonVariant.Ghost}
              size={NewButtonSize.Small}
              onlyIcon
              leftIcon={<DragDotsIcon />}
              aria-label={`${t("home.dragToReorder")}: ${columnLabel}`}
              title={t("home.dragToReorder")}
              tabIndex={-1}
            />
          </ColumnDragHandle>
          <ColumnEditLabel>{columnLabel}</ColumnEditLabel>
        </>
      )}
    </SortableColumnItemBox>
  );
};

const SideMenu = styled.div<{ isMenuOpen: boolean }>`
  position: absolute;
  z-index: 99999;
  height: 100vh;
  padding: ${(props) => (props.isMenuOpen ? "23px 24px" : "23px 0px")};
  right: 0;
  width: ${(props) => (props.isMenuOpen ? "400px" : "0px")};

  background: #202d38;
  transition: all 0.25s ease-in-out;
  color: rgba(163, 158, 157, 1);
  display: flex;
  /* opacity: ${(props) => (props.isMenuOpen ? "1" : "0")}; */
  flex-direction: column;

  gap: 9px;

  & > * {
    opacity: ${(props) => (props.isMenuOpen ? "1" : "0")};
    transition: all 0.125s ease-in-out;
  }

  &::before {
    content: "";
    position: absolute;
    top: 95px;
    left: 0;
    height: 48px;
    width: 5px;
    background-color: rgba(163, 158, 157, 0.7);
    left: ${(props) => (props.isMenuOpen ? "0" : "-50px")};
    transition: all 0.1s ease-in-out;
    background-color: ${(props) => (props.isMenuOpen ? "transparent" : "transparent")};
    position: ${(props) => (props.isMenuOpen ? "absolute" : "absolute")};
  }

  & > div:nth-child(1) {
    width: 100%;
    display: flex;
    gap: 9px;
    max-height: 100%;
    overflow-y: auto;
    overflow-x: hidden;

    & > p {
      text-align: center;
      font-size: 15px;
      font-weight: 400;
      letter-spacing: 0.225px;
    }
  }

  & > div:nth-child(2) {
    width: 100%;
    height: 48px;
    display: flex;
    justify-content: flex-end;
    gap: 5px;
    padding-top: 10px;

    & > div {
      order: 2;
    }
    & > button {
      order: 1;
    }
  }
`;

const ScrollTableBox = styled.div`
  width: 100%;
  flex-grow: 1;
  overflow-y: auto;

  &::-webkit-scrollbar {
    background: rgba(0, 145, 255, 0.09);
    width: 14px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 145, 255, 0.15);

    border: 3px solid transparent;
    background-clip: content-box;
    border-radius: 8px;
  }
`;

const Container = styled.div`
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const TableBox = styled.div`
  width: 100%;
`;

const TemplateBox = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  background: rgba(0, 0, 0, 0.5);

  & > div {
    height: 100%;
    display: flex;
    align-items: center;

    & > p {
      color: #f8f8f8;
      font-size: 18px;
      font-weight: 400;
      line-height: 24px;
      letter-spacing: -0.7px;
    }
  }
`;

enum ActivityLevel {
  Club = 0,
  Representation = 1,
  Own = 2,
}

interface filterObj {
  Name: string;
  AcademyId: string;
}

const HOME_FILTERS_LS_KEY = "homeFilters:v1";
const DEFAULT_MATCH_LEVELS = [ActivityLevel.Own, ActivityLevel.Club, ActivityLevel.Representation];

interface HomeFilters {
  academyBranch: { value: string; label: string } | null;
  academyFolder: { value: number; label: string } | null;
  isTreningChecked: boolean;
  isGameChecked: boolean;
  isTrendChecked: boolean;
  useGroupScope: boolean;
  selectedMatches: ActivityLevel[];
}

const readHomeFilters = (): HomeFilters => {
  const saved = getLocalStorageValue(HOME_FILTERS_LS_KEY, null);
  const training = typeof saved?.isTreningChecked === "boolean" ? saved.isTreningChecked : true;
  const game = typeof saved?.isGameChecked === "boolean" ? saved.isGameChecked : true;
  const levels = Array.isArray(saved?.selectedMatches)
    ? DEFAULT_MATCH_LEVELS.filter((level) => saved.selectedMatches.includes(level))
    : DEFAULT_MATCH_LEVELS;
  const branch = saved?.academyBranch;
  const folder = saved?.academyFolder;
  return {
    academyBranch: branch && typeof branch.label === "string" &&
      (typeof branch.value === "string" || typeof branch.value === "number")
      ? { value: String(branch.value), label: branch.label } : null,
    academyFolder: folder && typeof folder.label === "string" && typeof folder.value === "number"
      ? folder : null,
    isTreningChecked: training || !game,
    isGameChecked: game || !training,
    isTrendChecked: typeof saved?.isTrendChecked === "boolean" ? saved.isTrendChecked : true,
    useGroupScope: typeof saved?.useGroupScope === "boolean" ? saved.useGroupScope : false,
    selectedMatches: levels.length ? levels : [...DEFAULT_MATCH_LEVELS],
  };
};

const Home: React.FC = () => {
  const { t } = useTranslation();
  const [savedFilters] = useState(readHomeFilters);
  const [allUsers, setAllUsers] = useState<IPlayerStats[]>([]);
  const [avgStats, setAvgStats] = useState<IAvgStats>(avgHomeStatsInitialValues);
  const { isNavbarOpen, toggleNavbar } = useNavbarContext();
  const [trendStats, setTrendStats] = useState<any | null>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [tokenUser, setTokenUser] = useState("");

  const [debounceSearchInput] = useDebounce(searchQuery.trim(), 500);

  const handleSearchQueryChange = (e: any) => {
    setSearchQuery(e.target.value);
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      setTokenUser(token);
    }
  }, []);
  const [viewType] = useState<"list" | "grid">("list");

  const [isMenuOpen, toggleMenuOpen] = useState(false);
  const [isLoading, setIsloading] = useState(true);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [academyBranch, setAcademyBranch] = useState(savedFilters.academyBranch);
  const { academyBranches, ensureAcademyBranchesLoaded } = useAcademyBranches();

  const [homeColsCheckboxes, setHomeColsCheckboxes] = useState<CheckboxesState>(() => {
    const saved = localStorage.getItem(HOME_COLS_LS_KEY);
    if (!saved) return initialCheckboxes;

    try {
      return normalizeHomeColsCheckboxes(JSON.parse(saved) as Partial<CheckboxesState>);
    } catch {
      return initialCheckboxes;
    }
  });
  const [homeColumnOrder, setHomeColumnOrder] = useState<CheckboxKey[]>(() => {
    const saved = localStorage.getItem(HOME_COLS_ORDER_LS_KEY);
    if (!saved) return [...DEFAULT_HOME_COLUMN_ORDER];

    try {
      return normalizeHomeColumnOrder(JSON.parse(saved));
    } catch {
      return [...DEFAULT_HOME_COLUMN_ORDER];
    }
  });
  const [isColumnOrderEditMode, setIsColumnOrderEditMode] = useState(false);
  const columnDragSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const editableColumnOrder = useMemo(
    () => homeColumnOrder.filter((key) => !LOCKED_HOME_COLUMN_KEYS.includes(key)),
    [homeColumnOrder]
  );

  const [selectedWeeks, setSelectedWeeks] = useState<number[]>([]);

  useEffect(() => {
    localStorage.setItem(HOME_COLS_LS_KEY, JSON.stringify(homeColsCheckboxes));
  }, [homeColsCheckboxes]);

  useEffect(() => {
    setHomeColumnOrder((currentOrder) => {
      const normalizedOrder = normalizeHomeColumnOrder(currentOrder);
      const isAlreadyNormalized =
        normalizedOrder.length === currentOrder.length &&
        normalizedOrder.every((key, index) => key === currentOrder[index]);

      return isAlreadyNormalized ? currentOrder : normalizedOrder;
    });
  }, []);

  useEffect(() => {
    localStorage.setItem(HOME_COLS_ORDER_LS_KEY, JSON.stringify(homeColumnOrder));
  }, [homeColumnOrder]);

  const [academyFolders, setAcademyFolders] = useState<{ value: number; label: string }[]>([]);

  const [academyFolder, setAcademyFolder] = useState(savedFilters.academyFolder);

  const getAcademyMainName = useSelector((state: any) => state.currentAcademyIDReducer.academyName);

  const handleCheckboxChange = (key: CheckboxKey, checked: boolean) => {
    setHomeColsCheckboxes((prev) => {
      const item = prev[key];
      if (!item) return prev;
      if (item.locked) return prev;
      return {
        ...prev,
        [key]: {
          ...item,
          active: checked,
        },
      };
    });
  };

  const handleColumnDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    const activeKey = active.id as CheckboxKey;
    const overKey = over.id as CheckboxKey;
    const activeItem = homeColsCheckboxes[activeKey];
    const overItem = homeColsCheckboxes[overKey];

    if (!activeItem || !overItem || activeItem.locked || overItem.locked) {
      return;
    }

    setHomeColumnOrder((currentOrder) => {
      const currentEditableOrder = currentOrder.filter(
        (key) => !LOCKED_HOME_COLUMN_KEYS.includes(key)
      );
      const oldIndex = currentEditableOrder.indexOf(activeKey);
      const newIndex = currentEditableOrder.indexOf(overKey);

      if (oldIndex === -1 || newIndex === -1) return currentOrder;
      const movedEditableOrder = arrayMove(currentEditableOrder, oldIndex, newIndex);
      let editableIndex = 0;

      return DEFAULT_HOME_COLUMN_ORDER.map((key) =>
        LOCKED_HOME_COLUMN_KEYS.includes(key) ? key : movedEditableOrder[editableIndex++]
      );
    });
  };

  const [academyNameAvgData, setAcademyNameAvgData] = useState(getAcademyMainName);

  useEffect(() => {
    if (academyBranch === null) {
      setAcademyNameAvgData(getAcademyMainName);
    }

    if (academyBranch?.value !== "1-" && academyBranch !== null) {
      setAcademyNameAvgData(academyBranch?.label);
    }

    if (academyBranch?.value === "-1") {
      setAcademyNameAvgData(getAcademyMainName);
    }

    if (academyFolder) {
      setAcademyNameAvgData(academyFolder?.label);
    }
  }, [getAcademyMainName, academyBranch, academyNameAvgData, academyFolder]);

  const getAcademyMainID = useSelector((state: any) => state.currentAcademyIDReducer.AcademyId);

  const sort = useMemo(
    () => [
      { label: t("home.lastActivity"), value: 4 },
      { label: t("home.currentMicrocycle"), value: 0 },
      { label: t("home.currentVsPreviousMicrocycle"), value: 5 },
      { label: t("home.previousMicrocycle"), value: 1 },
      { label: t("home.mesocycle"), value: 3 },
      { label: t("home.microcycle"), value: 7 },
      { label: t("home.currentSeason"), value: 8 },
    ],
    [t]
  );

  const [sortByDay, setSortByDay] = useState<{
    value: number;
    label: string;
  } | null>(null);

  useEffect(() => {
    setSortByDay((prev) => {
      const fallback = sort.find((option) => option.value === 4) ?? sort[0];
      if (!prev) return fallback;
      const matched = sort.find((option) => option.value === prev.value);
      return matched ?? fallback;
    });
  }, [sort]);

  const [isTreningChecked, setIsTreningChecked] = useState(savedFilters.isTreningChecked);
  const [isGameChecked, setIsGameChecked] = useState(savedFilters.isGameChecked);
  const [isTrendChecked, setIsTrendChecked] = useState(savedFilters.isTrendChecked);
  const [useGroupScope, setUseGroupScope] = useState(savedFilters.useGroupScope);

  const playerActivity = isTreningChecked ? (isGameChecked ? 5 : 0) : 4;

  const [selectedMatches, setSelectedMatches] = useState(savedFilters.selectedMatches);

  const hasActiveFilters =
    (academyBranch != null && String(academyBranch.value) !== String(getAcademyMainID)) ||
    (academyFolder?.value != null && String(academyFolder.value) !== "") ||
    useGroupScope ||
    !isTrendChecked ||
    !isTreningChecked ||
    !isGameChecked ||
    DEFAULT_MATCH_LEVELS.some((level) => !selectedMatches.includes(level)) ||
    DEFAULT_HOME_COLUMN_ORDER.some(
      (key) => homeColsCheckboxes[key].active !== initialCheckboxes[key].active
    );

  useEffect(() => {
    setLocalStorageValue(HOME_FILTERS_LS_KEY, {
      academyBranch,
      academyFolder,
      isTreningChecked,
      isGameChecked,
      isTrendChecked,
      useGroupScope,
      selectedMatches,
    });
  }, [
    academyBranch, academyFolder, isTreningChecked, isGameChecked,
    isTrendChecked, useGroupScope, selectedMatches,
  ]);
  const toggleMatch = (value: ActivityLevel) => {
    const isSelected = selectedMatches.includes(value);

    if (isSelected && selectedMatches.length === 1) return;

    if (isSelected) {
      setSelectedMatches(selectedMatches.filter((v) => v !== value));
    } else {
      setSelectedMatches([...selectedMatches, value]);
    }
  };
  const homeURL = "/api/StatisticsProcessor/GetAggregatedStatisticsByAcademyId";
  const groupURL = "/api/StatisticsProcessor/GetAggregatedStatisticsByGroupId";

  const academyId = useMemo(() => {
    if (!academyBranch) {
      return getAcademyMainID;
    }
    return academyBranch.value;
  }, [academyBranch, getAcademyMainID]);

  const cancelTokenSourceRef = useRef<import("axios").CancelTokenSource | null>(null);

  useEffect(() => {
    if (!academyId) {
      console.log("skipped: no academyId");
      return;
    }

    if (sortByDay?.value === 7) {
      fetchRef.current.lastCompletedParams = undefined;
      return;
    }

    if (!tokenUser) {
      console.log("skipped: no tokenUser yet");
      return;
    }

    if (sortByDay?.value == null) {
      console.log(" sortByDay.value is null/undefined  using fallback?");
    }

    const params = {
      academyId,
      microcycle: sortByDay?.value ?? 4,
      activity: playerActivity,
      levels: selectedMatches,
      folder: academyFolder?.value ?? "",
      branch: academyBranch?.value ?? academyId,
      WeekNumbers: [],
      Year: new Date().getFullYear(),
      UseGroupScope: useGroupScope,
    };

    const paramsKey = JSON.stringify(params);

    if (
      fetchRef.current.lastCompletedParams === paramsKey ||
      fetchRef.current.pendingParams === paramsKey ||
      fetchRef.current.inFlightParams === paramsKey
    ) {
      console.log(paramsKey);
      return;
    }

    if (cancelTokenSourceRef.current) {
      cancelTokenSourceRef.current.cancel("New fetch triggered - cancelling old one");
      console.log("cancelled previous request");
    }

    const source = axios.CancelToken.source();
    cancelTokenSourceRef.current = source;

    fetchRef.current.pendingParams = paramsKey;

    const timer = setTimeout(() => {
      if (fetchRef.current.pendingParams !== paramsKey) {
        return;
      }

      fetchRef.current.pendingParams = undefined;
      fetchRef.current.inFlightParams = paramsKey;

      fetchPlayers(source.token).then((succeeded) => {
        if (fetchRef.current.inFlightParams !== paramsKey) {
          return;
        }

        fetchRef.current.inFlightParams = undefined;
        if (succeeded) {
          fetchRef.current.lastCompletedParams = paramsKey;
        }
      });
    }, 150);

    return () => {
      clearTimeout(timer);
      if (fetchRef.current.pendingParams === paramsKey) {
        fetchRef.current.pendingParams = undefined;
      }

      if (fetchRef.current.inFlightParams === paramsKey) {
        fetchRef.current.inFlightParams = undefined;
      }

      if (cancelTokenSourceRef.current === source) {
        cancelTokenSourceRef.current.cancel("Cleanup / deps changed");
        console.log("[fetch cleanup] cancelled pending request");
      }
    };
  }, [
    academyId,
    tokenUser,
    sortByDay?.value,
    playerActivity,
    selectedMatches,
    academyFolder?.value,
    academyBranch?.value,
    useGroupScope,
  ]);

  const fetchPlayers = async (
    cancelToken?: import("axios").CancelToken,
    override?: {
      academyId?: string;
      selectedGroupId?: string | number | null;
      microcycle?: number;
      activity?: number;
      levels?: ActivityLevel[];
      weekNumbers?: number[];
      useGroupScope?: boolean;
    }
  ) => {
    const effectiveAcademyId = override?.academyId ?? academyId;
    if (!effectiveAcademyId) {
      return false;
    }

    setIsloading(true);

    const selectedGroupId = override?.selectedGroupId ?? academyFolder?.value ?? null;
    const url = selectedGroupId ? groupURL : homeURL;

    const microcycle = override?.microcycle ?? sortByDay?.value ?? 4;
    const activity = override?.activity ?? playerActivity;
    const levels = override?.levels ?? selectedMatches;
    const weekNumbers = override?.weekNumbers ?? (microcycle === 7 ? selectedWeeks : []);
    const groupScope = override?.useGroupScope ?? useGroupScope;

    const requestBody = selectedGroupId
      ? {
          GroupId: selectedGroupId,
          AcademyId: effectiveAcademyId,
          Microcycle: microcycle,
          Activity: activity,
          Levels: levels,
          WeekNumbers: weekNumbers,
          Year: new Date().getFullYear(),
          UseGroupScope: groupScope,
        }
      : {
          AcademyId: effectiveAcademyId,
          Microcycle: microcycle,
          Activity: activity,
          Levels: levels,
          WeekNumbers: weekNumbers,
          Year: new Date().getFullYear(),
          UseGroupScope: groupScope,
        };

    try {
      const response = await api.post(url, requestBody, {
        cancelToken,
      });

      const avgStats = response.data?.Data?.AverageStatistics ?? {};
      const playersStatistics = response.data?.Data?.PlayersStatistics ?? [];
      const playerConditions = response.data?.Data?.PlayerConditions ?? [];
      const playertrends = response.data?.Data?.Trends ?? [];
      const timelineSummary = response.data?.Data?.TimelineSummary ?? [];

      const mergerData = playersStatistics.map((player: any) => {
        const condition = playerConditions.find((c: any) => c?.PlayerId === player?.PlayerId);
        const playerTimelineSummary = timelineSummary.find(
          (summary: any) => (summary?.playerId ?? summary?.PlayerId) === player?.PlayerId
        );

        let formValue: string | null = null;
        if (condition?.Form != null && condition.CreatedAt) {
          try {
            const d = new Date(condition.CreatedAt);
            if (!isNaN(d.getTime())) {
              formValue = computeFormDisplay(condition.Form, d, new Date()).toFixed(0);
            }
          } catch {}
        }

        return {
          ...player,
          Form: formValue,
          Readiness: condition?.Readiness ?? null,
          Potential: condition?.Potential ?? null,
          TimelineSummary: playerTimelineSummary ?? null,
        };
      });

      setAllUsers(mergerData);
      setAvgStats(avgStats);

      const filteredTrends = playertrends.filter((t: any) =>
        playersStatistics.some((p: any) => p.PlayerId === t.PlayerId)
      );

      setTrendStats({
        PlayerStatistics: {
          current: mergerData ?? [],
          prev: filteredTrends ?? [],
        },
      });
      return true;
    } catch (err: any) {
      if (axios.isCancel(err)) {
        console.log("cancelled:", err.message);
        return false;
      }

      console.error("error:", err);
      toast.error(t("home.loadDataError"));
    } finally {
      setIsloading(false);
      console.log("[fetchPlayers] finished");
    }
  };

  const previousGroupIdRef = useRef<string | number | null | undefined>(undefined);

  useEffect(() => {
    const currentGroupId = academyFolder?.value ?? null;
    const previousGroupId = previousGroupIdRef.current;
    previousGroupIdRef.current = currentGroupId;

    if (previousGroupId === undefined || previousGroupId === currentGroupId) {
      return;
    }

    if (sortByDay?.value !== 7 || !selectedWeeks.length || !tokenUser || !academyId) {
      return;
    }

    fetchPlayers(undefined, {
      selectedGroupId: currentGroupId,
      weekNumbers: selectedWeeks,
    });
  }, [academyFolder?.value, academyId, selectedWeeks, sortByDay?.value, tokenUser]);

  useEffect(() => {
    if (!getAcademyMainID) return;
    setAcademyBranch((current) => {
      const value = current?.value ?? getAcademyMainID;
      const option = academyBranches.find((item) => String(item.value) === String(value));
      const label = option?.label ?? current?.label ?? getAcademyMainName;
      if (current && current.label === label) return current;
      return { value: String(value), label };
    });
  }, [academyBranches, getAcademyMainID, getAcademyMainName]);

  const academyIdForGroups = useMemo(() => {
    if (academyBranch) {
      return academyBranch.value;
    }

    return getAcademyMainID;
  }, [academyBranch, getAcademyMainID]);

  const getGroupFolders = async (id: string) => {
    if (!id) {
      console.warn("No academy ID for fetching folders");
      return;
    }

    try {
      setIsloading(true);
      const resp = await GetOwnFolders(id);

      const mappedFolders = (resp.Data || []).map((folder: any) => ({
        label: folder.Name,
        value: folder.FolderId,
      }));

      setAcademyFolders([{ label: t("common.any"), value: "" }, ...mappedFolders]);
    } catch (error) {
      console.error("Error fetching group folders:", error);
      toast.error(t("home.loadGroupsError"));
      setAcademyFolders([]);
    } finally {
      setIsloading(false);
    }
  };

  useEffect(() => {
    if (academyBranch) {
      getGroupFolders(academyIdForGroups);
    } else {
      setAcademyFolders([]);
    }
  }, [academyBranch, academyIdForGroups, getAcademyMainID]);

  useEffect(() => {
    const anyLabel = t("common.any");
    setAcademyFolders((prev) => {
      if (!prev.length) return prev;
      return prev.map((folder) =>
        (folder.value as unknown) === "" ? { ...folder, label: anyLabel } : folder
      );
    });
    setAcademyFolder((prev) =>
      prev && (prev.value as unknown) === "" ? { ...prev, label: anyLabel } : prev
    );
  }, [t]);

  const fetchRef = useRef<{
    abort?: () => void;
    pendingParams?: string;
    inFlightParams?: string;
    lastCompletedParams?: string;
  }>({});

  useEffect(() => {
    if (isMenuOpen) {
      ensureAcademyBranchesLoaded();
    }
  }, [isMenuOpen, ensureAcademyBranchesLoaded]);

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const savedScrollPosition = localStorage.getItem("scrollPosition");
    if (savedScrollPosition && scrollRef.current) {
      const scrollPosition = parseInt(savedScrollPosition, 10);
      scrollRef.current.scrollLeft = scrollPosition;
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollRef.current) {
        localStorage.setItem("scrollPosition", String(scrollRef.current.scrollLeft));
      }
    };

    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const resetFilters = () => {
    setSortByDay(
      sort.find((option) => option.value === 0) ?? {
        label: t("home.currentMicrocycle"),
        value: 0,
      }
    );
    setSelectedMatches([...DEFAULT_MATCH_LEVELS]);
    setIsTreningChecked(true);
    setIsGameChecked(true);
    setIsTrendChecked(true);
    setUseGroupScope(false);
    setSelectedWeeks([]);

    ensureAcademyBranchesLoaded();

    setAcademyFolders([]);
    setAcademyFolder(null);
    setAcademyBranch(getAcademyMainID ? {
      value: String(getAcademyMainID),
      label: getAcademyMainName,
    } : null);
    setHomeColsCheckboxes(normalizeHomeColsCheckboxes());
    setHomeColumnOrder([...DEFAULT_HOME_COLUMN_ORDER]);
    setIsColumnOrderEditMode(false);

    fetchRef.current.pendingParams = undefined;
    fetchRef.current.inFlightParams = undefined;
    fetchRef.current.lastCompletedParams = undefined;
    if (cancelTokenSourceRef.current) {
      cancelTokenSourceRef.current.cancel("Reset filters - cancelling old one");
    }
  };

  const getExcelexport = async () => {
    if (!academyId) return;
    if (isExportLoading) return;

    const requestBody = {
      AcademyId: academyId,
      Microcycle: sortByDay?.value ?? 4,
      Activity: playerActivity,
      Levels: selectedMatches,
      WeekNumbers: selectedWeeks,
      Year: new Date().getFullYear(),
      UseGroupScope: useGroupScope,
    };

    try {
      setIsExportLoading(true);

      const baseUrl = getOtherConnectionUrl("justwin");
      const URL = `${baseUrl}/statisticsprocessor/GetHomeRaportToExcel`;
      const response = await axios.post(URL, requestBody, {
        headers: {
          accept: "*/*",
        },
        responseType: "blob",
      });

      const contentDisposition =
        response.headers["content-disposition"] ?? response.headers["Content-Disposition"];
      const fileNameMatch = contentDisposition?.match(/filename\*?=(?:UTF-8''|")?([^\";]+)/i);
      const fileName = decodeURIComponent(
        fileNameMatch?.[1]?.replace(/"/g, "") ??
          `${t("home.activity")}_${new Date().toISOString().split("T")[0]}.xlsx`
      );

      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (errors: any) {
      console.error(errors);
      toast.error(t("home.exportXlsxError"));
    } finally {
      setIsExportLoading(false);
    }
  };

  const getWeeksInMonth = (weeks: number[]) => {
    setSelectedWeeks(weeks);
  };

  useEffect(() => {
    if (!isMenuOpen) {
      setIsColumnOrderEditMode(false);
    }
  }, [isMenuOpen]);

  return (
    <>
      <BlurBox isMenuOpen={isMenuOpen} onClick={() => toggleMenuOpen(false)} />
      <SideMenu isMenuOpen={isMenuOpen}>
        <div
          style={{
            height: "100%",
            position: "relative",
          }}
        >
          <ScrollContainers
            style={{
              background: "rgb(7, 16, 23)",
              border: "1px solid rgb(85, 104, 120)",
              borderRadius: "15px",
            }}
          >
            <SingleColumn
              style={{
                alignItems: "center",
                width: "100%",
                padding: "20px 0px",
                overflowY: "auto",
              }}
            >
              <div
                style={{
                  paddingBottom: "10px",
                }}
              >
                {t("home.selectAcademy")}
              </div>

              <NewSelectStatystics
                name="AcademyId"
                items={academyBranches}
                label={t("nav.club")}
                style={{
                  width: "280px",
                  height: "48px",
                  borderRadius: "8px",
                }}
                // @ts-ignore

                selectedItem={academyBranch}
                setSelectedItem={(branch) => {
                  setAcademyBranch(branch);
                  setAcademyFolder(null);
                }}
              />

              <>
                <div
                  style={{
                    padding: "10px 0",
                  }}
                >
                  {t("home.sortPlayersByGroups")}
                </div>
                <NewSelectStatystics
                  name="groups"
                  items={academyFolders}
                  label={t("home.groups")}
                  style={{
                    width: "280px",
                    height: "48px",
                    borderRadius: "8px",
                  }}
                  // @ts-ignore
                  selectedItem={academyFolder}
                  setSelectedItem={setAcademyFolder}
                />
              </>

              <div
                style={{
                  width: "280px",
                  paddingTop: "20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <div style={{ paddingBottom: "20px" }}>{t("home.groupScope")}</div>
                <NewCheckbox
                  checked={useGroupScope}
                  onChange={setUseGroupScope}
                  label={t("home.useGroupScope")}
                />
              </div>

              {/*  */}

              <div>
                <div
                  style={{
                    paddingTop: "15px",
                  }}
                >
                  {t("home.toggleTrend")}
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    justifyContent: "space-between",
                    width: "270px",
                    gap: "10px",
                    paddingTop: "20px",
                  }}
                >
                  <NewCheckbox
                    checked={isTrendChecked}
                    onChange={(checked) => {
                      setIsTrendChecked(checked);
                    }}
                    label={t("home.trend")}
                  />
                </div>

                <div
                  style={{
                    paddingTop: "20px",
                  }}
                >
                  {t("home.selectActivity")}
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    justifyContent: "space-between",
                    width: "250px",
                    gap: "10px",
                    paddingTop: "20px",
                  }}
                >
                  <NewCheckbox
                    checked={isTreningChecked}
                    onChange={(checked) => {
                      if (!checked && !isGameChecked) {
                        return;
                      }
                      setIsTreningChecked(checked);
                    }}
                    label={t("home.training")}
                  />

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "end",
                    }}
                  >
                    <NewCheckbox
                      checked={isGameChecked}
                      onChange={(checked) => {
                        if (!checked && !isTreningChecked) {
                          return;
                        }
                        setIsGameChecked(checked);
                      }}
                      label={t("home.match")}
                    />
                  </div>
                </div>

                <div
                  style={{
                    paddingTop: "25px",
                  }}
                >
                  {t("home.selectLevel")}
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    justifyContent: "space-between",
                    width: "270px",
                    gap: "10px",
                    paddingTop: "20px",
                  }}
                >
                  <NewCheckbox
                    checked={selectedMatches.includes(ActivityLevel.Own)}
                    onChange={() => toggleMatch(ActivityLevel.Own)}
                    label={t("home.individual")}
                  />

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "end",
                    }}
                  >
                    <NewCheckbox
                      checked={selectedMatches.includes(ActivityLevel.Club)}
                      onChange={() => toggleMatch(ActivityLevel.Club)}
                      label={t("home.clubLevel")}
                    />
                  </div>

                  <NewCheckbox
                    checked={selectedMatches.includes(ActivityLevel.Representation)}
                    onChange={() => toggleMatch(ActivityLevel.Representation)}
                    label={t("home.national")}
                  />
                </div>

                {/*  */}

                <div
                  style={{
                    paddingTop: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <NewButton
                    variant={NewButtonVariant.Ghost}
                    size={NewButtonSize.Small}
                    onlyIcon
                    leftIcon={
                      !isColumnOrderEditMode ? (
                        <EditIcon
                          style={{
                            width: "16px",
                            height: "16px",
                          }}
                        />
                      ) : (
                        <CloseIcon
                          style={{
                            width: "10px",
                            height: "10px",
                            color: "white",
                          }}
                        />
                      )
                    }
                    aria-label={
                      isColumnOrderEditMode
                        ? t("home.finishColumnOrder")
                        : t("home.editColumnOrder")
                    }
                    title={
                      isColumnOrderEditMode
                        ? t("home.finishColumnOrderShort")
                        : t("home.editColumnOrder")
                    }
                    isActive={isColumnOrderEditMode}
                    style={{ order: 2, width: "32px", minWidth: "32px" }}
                    onClick={() => setIsColumnOrderEditMode((current) => !current)}
                  />
                  {isColumnOrderEditMode ? t("home.sortColumns") : t("home.editColumnOrder")}
                </div>

                <ColumnListBox isEditMode={isColumnOrderEditMode}>
                  <DndContext
                    sensors={columnDragSensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleColumnDragEnd}
                  >
                    {isColumnOrderEditMode ? (
                      <>
                        {LOCKED_HOME_COLUMN_KEYS.map((columnKey) => (
                          <LockedColumnItem
                            key={columnKey}
                            columnKey={columnKey}
                            item={homeColsCheckboxes[columnKey]}
                            isEditMode
                            onCheckboxChange={handleCheckboxChange}
                          />
                        ))}
                        <SortableContext
                          items={editableColumnOrder}
                          strategy={verticalListSortingStrategy}
                        >
                          {editableColumnOrder.map((columnKey) => (
                            <SortableColumnItem
                              key={columnKey}
                              columnKey={columnKey}
                              item={homeColsCheckboxes[columnKey]}
                              isEditMode
                              onCheckboxChange={handleCheckboxChange}
                            />
                          ))}
                        </SortableContext>
                      </>
                    ) : (
                      [...LOCKED_HOME_COLUMN_KEYS, ...editableColumnOrder].map((columnKey) => (
                        <LockedColumnItem
                          key={columnKey}
                          columnKey={columnKey}
                          item={homeColsCheckboxes[columnKey]}
                          isEditMode={false}
                          onCheckboxChange={handleCheckboxChange}
                        />
                      ))
                    )}
                  </DndContext>
                </ColumnListBox>

                <div
                  style={{
                    paddingTop: "20px",
                  }}
                >
                  {t("home.resetFilters")}
                </div>

                <div
                  style={{
                    paddingTop: "10px",
                  }}
                >
                  <Button
                    variant={ButtonVariant.Remove}
                    onClick={() => resetFilters()}
                    style={{
                      width: "280px",
                    }}
                  >
                    {t("common.reset")}
                  </Button>
                </div>
              </div>
            </SingleColumn>
          </ScrollContainers>
        </div>
      </SideMenu>

      <Container>
        <TemplateBox>
          <div className=" w-full">
            <div
              style={{
                height: "48px",
                width: "100%",
                display: "flex",
                alignItems: "center",
              }}
            >
              <NewButton
                onlyIcon
                onClick={toggleNavbar}
                size={NewButtonSize.Medium}
                leftIcon={
                  isNavbarOpen ? (
                    <img src={CloseMenuIcon} alt="close" />
                  ) : (
                    <img src={OpenMenuIcon} alt="open" />
                  )
                }
                variant={NewButtonVariant.Secondary}
                outline
              />

              <p
                style={{
                  paddingLeft: "35px",
                  color: "rgb(248, 248, 248)",
                  fontWeight: 400,
                  fontSize: " 18px",
                  letterSpacing: "0.1px",
                  height: "48px",
                }}
                className="flex items-center text-white"
              >
                {t("home.activity")}
              </p>
            </div>
            <div className="flex flex-wrap items-center  justify-end w-full" style={{ gap: "5px" }}>
              <NewButton
                style={{
                  height: "32px",
                }}
                isLoading={isExportLoading}
                isDisabled={isExportLoading || !academyId || allUsers.length === 0}
                onClick={getExcelexport}
              >
                {t("common.exportXlsx")}
              </NewButton>

              <SearchBox onChange={handleSearchQueryChange} />

              <FilterButtonWrapper style={{ marginRight: "24px" }}>
                <NewButton
                  size={NewButtonSize.Small}
                  onlyIcon
                  style={{ height: "32px", width: "32px" }}
                  leftIcon={<img src={FilterIco} alt="filter" />}
                  variant={NewButtonVariant.Secondary}
                  onClick={() => toggleMenuOpen((prev) => !prev)}
                />
                {hasActiveFilters && <ActiveFilterDot aria-label={t("events.activeFilters")} />}
              </FilterButtonWrapper>
            </div>
          </div>
        </TemplateBox>

        <>
          <div
            className="flex items-center"
            style={{
              background: "#08233a",
              borderLeft: "3px solid #020e19",
            }}
          >
            <div
              className="text-white  text-center pt-18 pb-18"
              style={{
                paddingLeft: "28px",
                paddingRight: "60px",
              }}
            >
              {t("home.period")}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                gap: "10px",
                zIndex: "9999",
              }}
            >
              <NewSelectStatystics
                name="Sort"
                items={sort}
                label=""
                selectedItem={sortByDay}
                setSelectedItem={setSortByDay}
                defaultValue={0}
                disableSort={true}
              />

              {sortByDay?.value === 7 && (
                <>
                  <WeeksMultiSelect value={selectedWeeks} onChange={getWeeksInMonth} />

                  <NewButton
                    variant={NewButtonVariant.Secondary}
                    size={NewButtonSize.Small}
                    isDisabled={!selectedWeeks?.length}
                    onClick={() => fetchPlayers()}
                    isLoading={isLoading}
                  >
                    {t("common.apply")}
                  </NewButton>
                </>
              )}
            </div>
          </div>

          <ScrollTableBox ref={scrollRef}>
            <TableBox>
              <HomeTableNew
                playerProfiles={allUsers}
                avgStats={avgStats || ({} as IAvgStats)}
                loading={isLoading}
                searchQuery={debounceSearchInput}
                mainAcademyName={academyNameAvgData}
                isTrendChecked={isTrendChecked}
                trendStats={trendStats}
                homeColsCheckboxes={homeColsCheckboxes}
                homeColumnOrder={homeColumnOrder}
                academyFolder={academyFolder}
              />
            </TableBox>
          </ScrollTableBox>
        </>
      </Container>
    </>
  );
};

export default Home;

const ScrollContainers = styled.div<{ items?: boolean }>`
  overflow-y: auto;
  max-height: 100%;
  border-radius: 8px;
  width: 100%;

  border: ${({ items }) => (!items ? "none" : "2px solid rgb(51, 68, 82)")};

  &::-webkit-scrollbar {
    width: 1px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #91a3b6;
    border-radius: 4px;
  }

  scrollbar-width: thin;
  scrollbar-color: #91a3b6 transparent;
`;
